import type { NotificationChannel } from "./Notifications";

// Sharp increase in probability (+40% jump in 1-3 hours)
export interface SpikeParams {
    threshold_percent: number;
}

// Sharp decrease in probability (-20% drop in 1-3 hours)
export interface DropParams {
    threshold_percent: number;
}

export interface CrossAnyParams {
    threshold_percent: number;
}

export interface CrossUpParams {
    threshold_percent: number;
}

export interface CrossDownParams {
    threshold_percent: number;
}

export interface GreaterThanParams {
    threshold_percent: number;
}

export interface LessThanParams {
    threshold_percent: number;
}

// Percentage move (e.g., 10% up or down)
export interface MovePctAnyParams {
    threshold_percent: number;
}

export interface MovePctUpParams {
    threshold_percent: number;
}

export interface MovePctDownParams {
    threshold_percent: number;
}

// Unusual fluctuations where std deviation of past 3 hours > 2x avg volatility
export interface VolatilitySurgeParams {
    deviationThreshold: number;
}

// Price moves beyond support/resistance levels in 6-12 hours
export interface BreakoutParams {
    lookbackHours: number;
}

// Trend reversal when 3-hour EMA crosses 6-hour EMA
export interface MomentumShiftParams {
    shortEMA: number;
    longEMA: number;
}

// Price jumps up/down by 10%+ without intermediate prices
export interface GapParams {
    threshold_percent: number;
}

// Volume spikes 3x normal (if volume data available)
export interface WhaleActivityParams {
    volumeMultiplier: number;
}

// Market reaches new maximum/minimum in historical data
export interface AllTimeRecordParams {
}

// Price consistently moves opposite to recent trend over 6-12 hours
export interface TrendReversalParams {
    lookbackHours: number;
}

// Empty params for notifications that don't need parameters
export interface EmptyParams {}

export interface SubscriptionConfig<T = EmptyParams> {
    params: T;
}

export enum TriggerType {
    ONE_TIME = "One Time",
    RECURRING = "Recurring"
}
/**
 * Subscription Types and Detection Logic
 * 
 * Spike 🚀
 * - Definition: Sharp increase in probability
 * - Window: 1-3 hours
 * - Logic: current price ≥ 1.2x previous hour price (20%+ jump)
 * 
 * Drop 📉
 * - Definition: Sharp decrease in probability
 * - Window: 1-3 hours
 * - Logic: current price ≤ 0.8x previous hour price (20%+ drop)
 * 
 * Cross ⚡
 * - Definition: Market crosses a key threshold (e.g., 50%)
 * - Window: 1-6 hours
 * - Logic: price moves from below to above 50% (or vice versa)
 * 
 * Created 🆕
 * - Definition: New market added
 * - Window: Hourly
 * - Logic: New entry appears in market data
 * 
 * Closed 🔒
 * - Definition: Market resolved or betting stops
 * - Window: Hourly
 * - Logic: Market disappears or status changes to "closed"
 * 
 * Volatility Surge 🌊
 * - Definition: Unusual fluctuations in price
 * - Window: 3-6 hours
 * - Logic: std deviation of past 3 hours > 2x avg volatility
 * 
 * Breakout 🚀📈
 * - Definition: Price moves beyond support/resistance
 * - Window: 6-12 hours
 * - Logic: current price > max of last 6 hours (or min for breakdown)
 * 
 * Momentum Shift 🔄
 * - Definition: Trend reversal in price movement
 * - Window: 6-12 hours
 * - Logic: 3-hour EMA crosses 6-hour EMA (bullish/bearish signal)
 * 
 * Gap Up/Down 📊
 * - Definition: Price jumps up/down without gradual movement
 * - Window: 1-3 hours
 * - Logic: current price - last price > 10% with no intermediate prices
 * 
 * Whale Activity 🐋
 * - Definition: Large volume trades cause sudden moves
 * - Window: 1-3 hours
 * - Logic: volume spikes 3x normal (if volume data is available)
 * 
 * All-Time High/Low 🔥
 * - Definition: Market reaches new max/min
 * - Window: Daily-Historical
 * - Logic: current price is highest/lowest in historical data
 * 
 * Trend Reversal 🔄📊
 * - Definition: Long-term direction changes
 * - Window: 6-12 hours
 * - Logic: price consistently moves opposite to recent trend
 */

export enum SubscriptionType {
    BIG_SPIKE = "Big Spike Z-score",
    BIG_DROP = "Big Drop",
    CROSSING = "Crossing",
    CROSSING_UP = "Crossing Up",
    CROSSING_DOWN = "Crossing Down",
    GREATER_THAN = "Greater Than",
    LESS_THAN = "Less Than",
    PCT_MOVE = "Percentage Move",
    PCT_MOVE_UP = "Percentage Move Up",
    PCT_MOVE_DOWN = "Percentage Move Down",
    VOLATILITY_SURGE = "Volatility Surge",
    BREAKOUT = "Breakout",
    MOMENTUM_SHIFT = "Momentum Shift",
    GAP = "Gap",
    WHALE_ACTIVITY = "Whale Activity",
    ALL_TIME_RECORD = "All-Time Record",
    TREND_REVERSAL = "Trend Reversal",
    EVENT_AND_MARKET_OPEN_STATUS_CHANGES = "New or closed events and markets"
}

export type SubscriptionTypeConfig = 
    | { type: SubscriptionType.BIG_SPIKE; config: SubscriptionConfig<SpikeParams> }          // 🚀 Sharp increase
    | { type: SubscriptionType.BIG_DROP; config: SubscriptionConfig<DropParams> }            // 📉 Sharp decrease
    | { type: SubscriptionType.CROSSING; config: SubscriptionConfig<CrossAnyParams> }           // ⚡ Crosses threshold
    | { type: SubscriptionType.CROSSING_UP; config: SubscriptionConfig<CrossUpParams> }        // ⚡ Crosses threshold
    | { type: SubscriptionType.CROSSING_DOWN; config: SubscriptionConfig<CrossDownParams> }      // ⚡ Crosses threshold
    | { type: SubscriptionType.GREATER_THAN; config: SubscriptionConfig<GreaterThanParams> }      // 🔄 Greater than
    | { type: SubscriptionType.LESS_THAN; config: SubscriptionConfig<LessThanParams> }          // 🔄 Less than
    | { type: SubscriptionType.PCT_MOVE; config: SubscriptionConfig<MovePctAnyParams> }        // 🔄 Percentage move
    | { type: SubscriptionType.PCT_MOVE_UP; config: SubscriptionConfig<MovePctUpParams> }     // 🔄 Percentage move
    | { type: SubscriptionType.PCT_MOVE_DOWN; config: SubscriptionConfig<MovePctDownParams> }   // 🔄 Percentage move
    | { type: SubscriptionType.VOLATILITY_SURGE; config: SubscriptionConfig<VolatilitySurgeParams> }  // 🌊 Unusual fluctuations
    | { type: SubscriptionType.BREAKOUT; config: SubscriptionConfig<BreakoutParams> }       // 🚀📈 Beyond support/resistance
    | { type: SubscriptionType.MOMENTUM_SHIFT; config: SubscriptionConfig<MomentumShiftParams> }  // 🔄 Trend reversal
    | { type: SubscriptionType.GAP; config: SubscriptionConfig<GapParams> }                 // 📊 Price gap up/down
    | { type: SubscriptionType.WHALE_ACTIVITY; config: SubscriptionConfig<WhaleActivityParams> }  // 🐋 Large volume trades
    | { type: SubscriptionType.ALL_TIME_RECORD; config: SubscriptionConfig }                // 🔥 New high/low
    | { type: SubscriptionType.TREND_REVERSAL; config: SubscriptionConfig<TrendReversalParams> } // 🔄📊 Direction change
    | {type: SubscriptionType.EVENT_AND_MARKET_OPEN_STATUS_CHANGES; config: SubscriptionConfig };

export enum SubscriptionStatusReason {
    CREATED = "Created",
    UPDATED = "Updated",
    EXPIRED = "Expired",
    TRIGGERED = "Triggered",
    PAUSED_BY_USER = "Paused by user",
    PAUSED_NO_MARKETS = "Paused (no markets)",
    PAUSED_NO_CHANNELS = "Paused (no channels)",
    RESTARTED_BY_USER = "Restarted by user",
    EVENT_CLOSED = "Event closed", 
    MARKET_CLOSED = "Market closed"
}

export interface SubscriptionStatus {
    enabled: boolean; // Whether the subscription is enabled
    reason: SubscriptionStatusReason;
}

/*
    1 subscription per scope
    2. create materialized view
    3. if trigger is once, materialized view updates will set the original subscription status to false
 */

export interface SubscriptionEventMap {
    [key: string]: {
        markets: string[]
    }
}

export enum SubscriptionScope {
    WATCHLIST = 'Watchlist',
    EVENT = 'Event',
    MARKET = 'Market'
}

export interface Subscription {
    watchlistId: string;
    watchlistName: string;

    subscriptionScope: SubscriptionScope; // The scope of the subscription (watchlist, event, market)
    subscriptionTypeConfig: SubscriptionTypeConfig; // The type of subscription and its configuration
    
    // If scope is watchlist events will be ignored on the API side and the watchlist items will be fetched instead
    // If scope is event, event key will be used, but markets will be fetched from the watchlist on the API side
    // If scope is market, events will be used to fetch the market data
    events: SubscriptionEventMap;
   
    createdAt: string; // ISO 8601
    expiresAt: string | null; // ISO 8601
    updatedAt?: string | null; // ISO 8601

    status: SubscriptionStatus;
    triggerType: TriggerType;
    triggerCount?: number;
    lastTriggeredAt?: string | null; // ISO 8601

    channels: NotificationChannel[];
}

export interface SubscriptionSchema extends Subscription {
    userId: string;
}

// triggerCount updates to source Subscription do not
// propagate to MaterializedSubscriptionEntry
export interface MaterializedSubscriptionEntry {
    userId: string;

    watchlistId: string;
    watchlistName: string;

    subscriptionId: string;

    subscriptionScope: SubscriptionScope;
    subscriptionTypeConfig: SubscriptionTypeConfig;

    eventId: string;
    eventTitle: string;

    marketId: string;
    marketQuestion: string;

    polymarketSlug: string;

    createdAt: string; // ISO 8601
    expiresAt: string | null; // ISO 8601
    updatedAt?: string | null; // ISO 8601

    status: SubscriptionStatus;
    triggerType: TriggerType;
    triggerCount?: number;
    lastTriggeredAt?: string | null; // ISO 8601

    channels: NotificationChannel[];
  }

export interface MaterializedSubscriptionSchema {
    marketId: string;
    eventId: string;
    clobTokenId: string;
    subscriptions: Array<MaterializedSubscriptionEntry>;
};

export interface GetSubscriptionsJSONRequest {
}

export interface SubscriptionResponse {
    watchlistId: string;
    watchlistName: string;

    subscriptionId: string;

    eventTitle?: string;
    eventId?: string;
    marketQuestion?: string;
    marketId?: string;

    subscriptionScope: SubscriptionScope;
    subscriptionTypeConfig: SubscriptionTypeConfig;

    createdAt: string;
    expiresAt: string | null;
    updatedAt?: string | null;

    status: SubscriptionStatus;
    triggerType: TriggerType;
    triggerCount?: number;
    lastTriggeredAt?: string | null; // ISO 8601
    
    channels: NotificationChannel[];
}

export interface GetSubscriptionsJSONResponse {
    subscriptions: SubscriptionResponse[];
}

export interface CreateSubscriptionJSONRequest {
    subscription: Subscription;
}

export interface CreateSubscriptionJSONResponse {
    subscriptionId: string;
}

export interface UpdateSubscriptionJSONRequest {
    subscription: Partial<Subscription>;
}

export interface UpdateSubscriptionJSONResponse {
    subscriptionId: string;
}



